import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Leaf,
  Globe,
  Trees,
  Award,
  Users,
  Target,
  HeartHandshake,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  Clock,
  Download,
  Apple,
  Wheat,
  Shield,
  Factory,
  Ship,
  Warehouse,
  Star,
  ShieldCheck,
  TrendingUp,
  MapPin,
  Calendar,
  Sprout,
  Droplets,
  Sun,
  Wind,
} from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from './../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const productCategories = [
  {
    id: 'vegetables',
    name: 'Vegetables',
    icon: Leaf,
    description:
      'Premium quality vegetables sourced from sustainable farms worldwide. Our vegetable exports meet the highest international standards for freshness, taste, and nutritional value.',
    count: 6,
    featured: ['Yellow Onions', 'Spring Onions', 'Sweet Potatoes', 'Capsicum'],
    link: '/products/vegetables',
    gradient: 'var(--gradient-primary)',
  },
  {
    id: 'fruits',
    name: 'Fruits',
    icon: Apple,
    description:
      'Premium quality fruits from the world’s best growing regions. Hand-picked at optimal ripeness to ensure perfect flavor, aroma, and nutritional value.',
    count: 5,
    featured: ['Strawberries', 'Table Grapes', 'Mangoes', 'Oranges'],
    link: '/products/fruits',
    gradient: 'var(--gradient-primary)',
  },
  {
    id: 'crops',
    name: 'Crops & Grains',
    icon: Wheat,
    description:
      'High-value specialty crops and grains for international markets. Grown using sustainable practices to meet the demands of discerning customers worldwide.',
    count: 3,
    featured: ['Lima Beans', 'Rice', 'Premium Peanuts'],
    link: '/products/crops',
    gradient: 'var(--gradient-primary)',
  },
];

const statsData = [
  {
    value: '12',
    label: 'Countries',
    suffix: '+',
    icon: Globe,
    description: 'Global reach across continents',
  },
  {
    value: '500',
    label: 'Partner Farms',
    suffix: '+',
    icon: Users,
    description: 'Sustainable smallholder farmers',
  },
  {
    value: '98',
    label: 'Customer Satisfaction',
    suffix: '%',
    icon: Star,
    description: 'Based on client feedback',
  },
  {
    value: '100',
    label: 'Quality Score',
    suffix: '%',
    icon: ShieldCheck,
    description: 'GlobalGAP certified',
  },
];

const certifications = [
  { name: 'GlobalGAP', icon: Shield },
  { name: 'ISO 9001', icon: Award },
  { name: 'Organic', icon: Leaf },
  { name: 'Fair Trade', icon: HeartHandshake },
];

export default function Profile() {
  const [expandedValues, setExpandedValues] = useState([]);
  const [isVisible, setIsVisible] = useState({});
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  const sectionRefs = {
    hero: useRef(null),
    stats: useRef(null),
    mission: useRef(null),
    values: useRef(null),
    products: useRef(null),
    process: useRef(null),
    certifications: useRef(null),
  };

  useEffect(() => {
    document.title = 'Profile - Alvanta';

    const observers = {};

    Object.keys(sectionRefs).forEach((key) => {
      if (!sectionRefs[key].current) return;
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          setIsVisible((prev) => ({
            ...prev,
            [key]: entry.isIntersecting,
          }));
        },
        { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
      );
      observers[key].observe(sectionRefs[key].current);
    });

    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const values = [
    {
      icon: HeartHandshake,
      title: 'Farmer Empowerment',
      shortDescription: 'Fair trade partnerships with 500+ smallholder farmers',
      fullDescription:
        'We eliminate middlemen and ensure 85%+ of revenue returns directly to farmers. Our comprehensive training programs in regenerative agriculture and direct market access create sustainable livelihoods for rural communities.',
      benefits: [
        'Fair pricing guarantee',
        'Technical training programs',
        'Direct market access',
        'Community development',
      ],
      impact: '500+ families benefited',
    },
    {
      icon: Trees,
      title: 'Environmental Stewardship',
      shortDescription: 'Carbon-neutral operations and biodiversity protection',
      fullDescription:
        'Every shipment is carbon-offset through verified programs. We restore degraded land and implement water conservation technologies for sustainable farming practices that protect our planet for future generations.',
      benefits: [
        'Carbon offset program',
        'Land restoration',
        'Water conservation',
        'Biodiversity protection',
      ],
      impact: '30% water reduction',
    },
    {
      icon: Target,
      title: 'Quality Excellence',
      shortDescription: 'Global certifications and blockchain traceability',
      fullDescription:
        'Real-time tracking via blockchain technology from seed to shelf. Third-party audited for food safety, freshness, and sustainability standards across all markets with complete transparency.',
      benefits: [
        'GlobalGAP certified',
        'ISO 9001:2024',
        'Blockchain tracking',
        'Premium quality',
      ],
      impact: '100% traceable',
    },
    {
      icon: Globe,
      title: 'Global Integration',
      shortDescription: 'Serving 12+ countries with optimized logistics',
      fullDescription:
        'We navigate complex international regulations and optimize cold-chain logistics for maximum freshness while maintaining the highest quality standards across global markets.',
      benefits: [
        '12+ countries served',
        'Cold-chain logistics',
        'Regulatory compliance',
        'Fast delivery',
      ],
      impact: '98% on-time delivery',
    },
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Farm Selection & Training',
      description:
        'Partnering with certified smallholder farms and providing comprehensive training in sustainable agriculture practices.',
      icon: Users,
      duration: 'Ongoing',
      details: '500+ trained farmers',
    },
    {
      step: '02',
      title: 'Sustainable Cultivation',
      description:
        'Implementation of regenerative farming techniques and organic practices to ensure premium quality.',
      icon: Leaf,
      duration: 'Growth Cycle',
      details: 'Eco-friendly practices',
    },
    {
      step: '03',
      title: 'Quality Harvesting',
      description:
        'Precision harvesting at optimal maturity with immediate quality assessment and blockchain documentation.',
      icon: Award,
      duration: 'Harvest Season',
      details: 'Blockchain verified',
    },
    {
      step: '04',
      title: 'Processing & Packaging',
      description:
        'State-of-the-art processing facilities with temperature control and eco-friendly packaging solutions.',
      icon: Factory,
      duration: '24-48 hours',
      details: 'Zero waste process',
    },
    {
      step: '05',
      title: 'Global Logistics',
      description:
        'Optimized cold-chain transportation with carbon-neutral shipping options and real-time tracking.',
      icon: Ship,
      duration: '2-7 days',
      details: 'Carbon offset',
    },
    {
      step: '06',
      title: 'Market Delivery',
      description:
        'Final quality checks and delivery with complete documentation and blockchain-verified provenance.',
      icon: Warehouse,
      duration: '24 hours',
      details: '100% satisfaction',
    },
  ];

  const toggleValue = (index) => {
    setExpandedValues((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const AnimatedCounter = ({
    value,
    label,
    suffix = '',
    icon: Icon,
    description,
  }) => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            let start = 0;
            const end = parseInt(value);
            const duration = 2000;
            const increment = end / (duration / 16);
            const timer = setInterval(() => {
              start += increment;
              if (start >= end) {
                setCount(end);
                clearInterval(timer);
              } else {
                setCount(Math.floor(start));
              }
            }, 16);
          }
        },
        { threshold: 0.3 }
      );
      if (ref.current) observer.observe(ref.current);
      return () => ref.current && observer.unobserve(ref.current);
    }, [value, hasAnimated]);

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={hasAnimated ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -4 }}
        className="rounded-2xl p-6 border text-center transition-all duration-300 hover:shadow-lg"
        style={{
          background: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)',
        }}
      >
        <div
          className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--gradient-primary)' }}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>
        <div
          className="text-4xl font-bold mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {count}
          {suffix}
        </div>
        <div
          className="text-base font-semibold mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {label}
        </div>
        <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {description}
        </div>
      </motion.div>
    );
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      {/* HERO SECTION */}
      <section
        ref={sectionRefs.hero}
        className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden"
        style={{ background: 'var(--gradient-hero)' }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute -top-1/2 -right-1/2 w-full h-full rounded-full"
            style={{
              background:
                'radial-gradient(circle, var(--color-primary-100) 0%, transparent 70%)',
              opacity: 0.1,
            }}
          />
          <motion.div
            animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            className="absolute -bottom-1/2 -left-1/2 w-full h-full rounded-full"
            style={{
              background:
                'radial-gradient(circle, var(--color-primary-50) 0%, transparent 70%)',
              opacity: 0.1,
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <Badge
              className="mb-6 px-6 py-2.5 text-sm backdrop-blur-sm border"
              style={{
                background: 'var(--color-primary-50)',
                color: 'var(--color-primary)',
                borderColor: 'var(--color-primary-200)',
              }}
            >
              <Globe className="w-4 h-4 mr-2" />
              Trusted Global Exporter Since 2024
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-center leading-tight mb-6"
            style={{ color: 'var(--color-text-primary)' }}
          >
            From Egyptian Fields
            <span
              className="block mt-2"
              style={{
                background: 'var(--gradient-text)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              to Global Tables
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="max-w-2xl mx-auto text-center text-lg leading-relaxed"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Discover Alvanta Export, where sustainable Egyptian agriculture
            meets world-class quality, complete traceability, and ethical trade
            practices.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
          >
            <Button
              asChild
              size="lg"
              className="font-semibold group shadow-xl transition-all duration-300 hover:shadow-2xl"
              style={{ background: 'var(--gradient-primary)', color: 'white' }}
            >
              <a href="/Company Profile - Alvanta.pdf" download>
                <Download className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" />
                Download Company Profile (PDF)
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/contact')}
              className="font-semibold transition-all duration-300 hover:scale-105"
              style={{
                borderColor: 'var(--color-primary)',
                color: 'var(--color-primary)',
                background: 'transparent',
              }}
            >
              Contact Our Team
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section
        ref={sectionRefs.stats}
        className="py-20 px-6"
        style={{ background: 'var(--color-bg-secondary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible.stats ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-12"
          >
            <Badge
              className="mb-4"
              style={{
                background: 'var(--color-primary-50)',
                color: 'var(--color-primary)',
              }}
            >
              Our Impact
            </Badge>
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Impact in Numbers
            </h2>
            <p
              className="max-w-2xl mx-auto text-lg"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Building a sustainable future through ethical agriculture and
              global partnerships
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsData.map((stat, index) => (
              <AnimatedCounter key={index} {...stat} />
            ))}
          </div>
        </div>
      </section>

      {/* MISSION SECTION */}
      <section
        ref={sectionRefs.mission}
        className="py-20 px-6"
        style={{ background: 'var(--color-bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isVisible.mission ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <Badge
                className="mb-4"
                style={{
                  background: 'var(--color-primary-50)',
                  color: 'var(--color-primary)',
                }}
              >
                Our Mission
              </Badge>
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Connecting Egypt's Finest Produce with the World
              </h2>
              <p
                className="text-lg mb-6 leading-relaxed"
                style={{ color: 'var(--color-text-muted)' }}
              >
                At Alvanta Export, we're on a mission to revolutionize
                agricultural trade by creating a transparent, sustainable, and
                equitable supply chain that benefits farmers, customers, and the
                planet.
              </p>
              <div className="space-y-4">
                {[
                  'Empower smallholder farmers through fair trade',
                  'Deliver premium quality with full traceability',
                  'Promote sustainable farming practices',
                  'Build lasting global partnerships',
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isVisible.mission ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: 'var(--color-primary)' }}
                    />
                    <span style={{ color: 'var(--color-text-primary)' }}>
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isVisible.mission ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div
                className="relative rounded-2xl overflow-hidden shadow-2xl"
                style={{ background: 'var(--color-bg-secondary)' }}
              >
                <div className="aspect-square flex items-center justify-center p-12">
                  <div className="text-center">
                    <Sprout
                      className="w-32 h-32 mx-auto mb-4"
                      style={{ color: 'var(--color-primary)' }}
                    />
                    <p
                      className="text-sm"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Sustainable Egyptian Agriculture
                    </p>
                  </div>
                </div>
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(135deg, var(--color-primary-500) 0%, transparent 100%)',
                    opacity: 0.1,
                  }}
                />
              </div>
              <div
                className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full blur-2xl"
                style={{
                  background: 'var(--gradient-primary)',
                  opacity: 0.1,
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section
        ref={sectionRefs.values}
        className="py-20 px-6"
        style={{ background: 'var(--color-bg-secondary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible.values ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <Badge
              className="mb-4"
              style={{
                background: 'var(--color-primary-50)',
                color: 'var(--color-primary)',
              }}
            >
              Our Principles
            </Badge>
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Core Values
            </h2>
            <p
              className="max-w-3xl mx-auto text-lg"
              style={{ color: 'var(--color-text-muted)' }}
            >
              The principles that guide every decision, partnership, and
              shipment
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, i) => {
              const Icon = value.icon;
              const isExpanded = expandedValues.includes(i);
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isVisible.values ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -6 }}
                  onClick={() => toggleValue(i)}
                  className="group rounded-2xl p-8 border cursor-pointer transition-all duration-500 hover:shadow-xl"
                  style={{
                    background: 'var(--color-bg-primary)',
                    borderColor: isExpanded
                      ? 'var(--color-primary)'
                      : 'var(--color-border-primary)',
                  }}
                >
                  <div className="flex items-start gap-5">
                    <div
                      className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{ background: 'var(--gradient-primary)' }}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>

                    <div className="flex-1">
                      <h3
                        className="text-2xl font-bold mb-3 transition-colors group-hover:text-emerald-600"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {value.title}
                      </h3>

                      <p
                        className="text-base leading-relaxed mb-4"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {isExpanded
                          ? value.fullDescription
                          : value.shortDescription}
                      </p>

                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          transition={{ duration: 0.3 }}
                        >
                          <div
                            className="mt-6 pt-6 border-t"
                            style={{
                              borderColor: 'var(--color-border-primary)',
                            }}
                          >
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              {value.benefits.map((benefit, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-2 text-sm"
                                  style={{ color: 'var(--color-text-muted)' }}
                                >
                                  <CheckCircle
                                    className="w-4 h-4 flex-shrink-0"
                                    style={{ color: 'var(--color-primary)' }}
                                  />
                                  <span>{benefit}</span>
                                </div>
                              ))}
                            </div>
                            <div
                              className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full"
                              style={{
                                background: 'var(--color-primary-50)',
                                color: 'var(--color-primary)',
                              }}
                            >
                              <TrendingUp className="w-3 h-3" />
                              {value.impact}
                            </div>
                          </div>
                        </motion.div>
                      )}

                      <div
                        className="mt-4 flex items-center gap-2 text-sm font-semibold"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        {isExpanded ? 'Show less' : 'Learn more'}
                        <ChevronDown
                          className={`w-5 h-5 transition-transform duration-300 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR PRODUCTS */}
      <section
        ref={sectionRefs.products}
        className="py-20 px-6"
        style={{ background: 'var(--color-bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible.products ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-12"
          >
            <Badge
              className="mb-4"
              style={{
                background: 'var(--color-primary-50)',
                color: 'var(--color-primary)',
              }}
            >
              Our Portfolio
            </Badge>
            <h2
              className="text-4xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Product Categories
            </h2>
            <p
              className="max-w-2xl mx-auto text-lg"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Premium Egyptian produce exported to 12+ countries with full
              traceability
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {productCategories.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isVisible.products ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="group rounded-2xl border overflow-hidden transition-all duration-300 hover:shadow-xl"
                  style={{
                    background: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border-primary)',
                  }}
                >
                  <div
                    className="h-1.5"
                    style={{ background: 'var(--gradient-primary)' }}
                  />
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg"
                        style={{ background: 'var(--gradient-primary)' }}
                      >
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <Badge
                        variant="outline"
                        className="text-xs font-semibold"
                        style={{
                          borderColor: 'var(--color-border-primary)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {cat.count} products
                      </Badge>
                    </div>

                    <h3
                      className="text-2xl font-bold mb-3 transition-colors group-hover:text-emerald-600"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {cat.name}
                    </h3>
                    <p
                      className="text-sm mb-6 line-clamp-3"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {cat.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-8">
                      {cat.featured.map((item) => (
                        <span
                          key={item}
                          className="text-xs px-3 py-1.5 rounded-full border font-medium"
                          style={{
                            borderColor: 'var(--color-primary-200)',
                            color: 'var(--color-primary)',
                            background: 'var(--color-primary-50)',
                          }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>

                    <Button
                      asChild
                      className="w-full transition-all group-hover:shadow-lg"
                      variant="outline"
                    >
                      <Link to={cat.link}>
                        Explore {cat.name}
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isVisible.products ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <Button
              asChild
              size="lg"
              className="relative overflow-hidden group/btn transition-all duration-300 hover:shadow-xl"
              style={{
                background: 'var(--gradient-primary)',
                color: 'white',
              }}
            >
              <Link to="/products">
                <span className="relative z-10">View All Products</span>
                <ArrowRight className="w-4 h-4 ml-2 relative z-10 transition-transform group-hover/btn:translate-x-1" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* SUPPLY CHAIN PROCESS */}
      <section
        ref={sectionRefs.process}
        className="py-20 px-6"
        style={{ background: 'var(--color-bg-secondary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible.process ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-12"
          >
            <Badge
              className="mb-4"
              style={{
                background: 'var(--color-primary-50)',
                color: 'var(--color-primary)',
              }}
            >
              How We Work
            </Badge>
            <h2
              className="text-4xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              From Farm to Fork
            </h2>
            <p
              className="max-w-2xl mx-auto text-lg"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Our transparent and sustainable supply chain process
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible.process ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl p-6 border transition-all duration-300 hover:shadow-lg"
                  style={{
                    background: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border-primary)',
                  }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ background: 'var(--gradient-primary)' }}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span
                      className="font-mono text-3xl font-bold opacity-10"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {step.step}
                    </span>
                  </div>
                  <h4
                    className="font-bold text-xl mb-2"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {step.title}
                  </h4>
                  <p
                    className="text-sm mb-4"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    {step.description}
                  </p>
                  <div
                    className="flex items-center justify-between mt-4 pt-4 border-t"
                    style={{ borderColor: 'var(--color-border-primary)' }}
                  >
                    <div
                      className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full"
                      style={{
                        background: 'var(--color-primary-50)',
                        color: 'var(--color-primary)',
                      }}
                    >
                      <Clock className="w-3 h-3" />
                      {step.duration}
                    </div>
                    <span
                      className="text-xs"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {step.details}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS SECTION */}
      <section
        ref={sectionRefs.certifications}
        className="py-20 px-6"
        style={{ background: 'var(--color-bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible.certifications ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-12"
          >
            <Badge
              className="mb-4"
              style={{
                background: 'var(--color-primary-50)',
                color: 'var(--color-primary)',
              }}
            >
              Quality Assurance
            </Badge>
            <h2
              className="text-4xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Our Certifications
            </h2>
            <p
              className="max-w-2xl mx-auto text-lg"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Committed to the highest international standards
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {certifications.map((cert, i) => {
              const Icon = cert.icon;
              return (
                <motion.div
                  key={cert.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={
                    isVisible.certifications ? { opacity: 1, scale: 1 } : {}
                  }
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl p-6 text-center border transition-all duration-300 hover:shadow-lg"
                  style={{
                    background: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border-primary)',
                  }}
                >
                  <div
                    className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center"
                    style={{ background: 'var(--gradient-primary)' }}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h4
                    className="font-semibold text-lg"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {cert.name}
                  </h4>
                  <p
                    className="text-xs mt-2"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Certified
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'var(--gradient-primary-bold)' }}
        />
        <div
          className="absolute inset-0 opacity-10"
          style={{ background: 'var(--color-white)' }}
        >
          <div className="absolute top-0 left-0 w-64 h-64 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ color: 'var(--color-white)' }}
          >
            Ready to Partner with Excellence?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-emerald-100 text-lg mb-10 max-w-2xl mx-auto"
          >
            Join our growing network of satisfied global partners. Let's build a
            sustainable future together.
          </motion.p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="font-semibold transition-all duration-300 hover:scale-105"
              style={{
                background: 'var(--color-white)',
                color: 'var(--color-primary-700)',
              }}
            >
              <a href="/Company Profile - Alvanta.pdf" download>
                <Download className="mr-3 w-5 h-5" />
                Download PDF Profile
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white transition-all duration-300 hover:bg-white/10 hover:scale-105"
              onClick={() => navigate('/contact')}
            >
              Get in Touch
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
